/**
 * MIN-298 — createAgentChat: one face, two ways in.
 *
 * The package's bare command face: a factory closing over one transport
 * (auth + base URL + per-host thread defaults) and handing back one bound
 * `AgentChat` — createThread/deleteThread/listThreads/readTranscript. No
 * React import here on purpose: a headless eval harness, an `@ai`-annotation
 * bot, or `AgentChat.Provider` (which just holds one instance of this) all
 * drive the exact same implementation, so the headless and React paths
 * cannot drift.
 *
 * Sending is deliberately not part of this face (MIN-298 PR #546 review):
 * the engine's `/chat/threads/{id}/messages` POST requires Vercel AI SDK
 * trigger fields the SDK's own transport already produces (see
 * services/ai-engine/tests/test_chat_controller.py
 * test_body_without_sdk_trigger_returns_422); reimplementing that wire
 * format here would just be a second, unused, and looser (`unknown`-yielding)
 * copy of what `@ai-sdk/react` does. Sending stays on the AI SDK transport
 * until a later slice takes it deliberately.
 */
import type { ChatAuth, ChatFetch } from "./chat-fetch";
import { makeChatFetch } from "./chat-fetch";
import type { EngineMessage } from "./message";
import { type CreateChatThreadInput, createChatThread, deleteChatThread } from "./mutations";
import { evictThreadChat } from "./registry";
import { type ChatThread, type EngineThread, type ThreadId, toChatThread } from "./thread";
import { type Turn, turnFromHistory } from "./turn";

/** Structural transport port — declared here, never imported from an app.
 *  A `MedplumClient` satisfies the auth half as-is (ADR-0015). */
export interface AgentTransport {
  auth: ChatAuth;
  baseUrl: string;
  /** Applied under whatever a caller passes to `createThread`. */
  defaults?: CreateThreadInput;
}

export type CreateThreadInput = CreateChatThreadInput;

export interface AgentChat {
  createThread(seed?: CreateThreadInput): Promise<ChatThread>;
  deleteThread(id: ThreadId): Promise<void>;
  listThreads(): Promise<ChatThread[]>;
  readTranscript(id: ThreadId): Promise<Turn[]>;
}

async function fetchJson<T>(chatFetch: ChatFetch, path: string): Promise<T> {
  const res = await chatFetch(path);
  if (!res.ok) {
    throw new Error(`AI engine ${res.status} ${res.statusText} on ${path}`);
  }
  return (await res.json()) as T;
}

/** Builds one `AgentChat`, bound to `transport`. */
export function createAgentChat(transport: AgentTransport): AgentChat {
  const { auth, baseUrl, defaults = {} } = transport;
  const chatFetch = makeChatFetch(auth, baseUrl);

  return {
    createThread: (seed = {}) => createChatThread(auth, baseUrl, { ...defaults, ...seed }),

    deleteThread: async (id) => {
      await deleteChatThread(auth, baseUrl, id);
      evictThreadChat(id);
    },

    listThreads: async () => {
      const data = await fetchJson<EngineThread[]>(chatFetch, "/chat/threads");
      return data.map(toChatThread);
    },

    readTranscript: async (id) => {
      const data = await fetchJson<EngineMessage[]>(chatFetch, `/chat/threads/${id}/messages`);
      return data.map(turnFromHistory);
    },
  };
}
